import {
    LoginCredentials,
    LoginResponse,
    AuthResponse,
    WorkerShift,
} from '../types/auth';
import { WorkOrder } from '../types/workOrders';
import { ExpensesResponse } from '../types/expenses';
import { FolderDoc } from '../types/forms-docs';
import { useAuthStore } from '../store/authStore';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';

// export const API_BASE = 'https://api.fieldy.io';
export const API_BASE = 'https://api.ninjasgroup.io';
// export const API_BASE = 'https://ftp.fixframeconstruction.uz';

// Utility function to extract tenantId
function getTenantId(): number {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
        throw new Error('No access token available');
    }

    // Decode token to get tenantId
    const decodedToken: any = jwtDecode(accessToken);
    const tenantId = decodedToken?.info?.tenantId;

    if (!tenantId) {
        throw new Error('Unable to extract Tenant ID');
    }

    return tenantId;
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
        throw new Error('No access token available');
    }

    try {
        const headers: Record<string, string> = {
            Authorization: `Bearer ${accessToken}`,
        };

        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const finalHeaders = {
            ...options.headers,
            ...headers,
        };

        const response = await fetch(url, {
            ...options,
            headers: finalHeaders,
        });

        // Handle token refresh or unauthorized scenarios if needed
        if (response.status === 401) {
            // Force logout and redirect to login page
            const { logout } = useAuthStore.getState();
            await logout();
            window.location.href = '/login';
            throw new Error('Session expired. Please log in again.');
        }

        return response;
    } catch (error) {
        console.error('Fetch with auth error:', error);
        if (
            error instanceof Error &&
            error.message === 'Session expired. Please log in again.'
        ) {
            // Error already handled above
            return;
        }
        throw error;
    }
}

export async function loginWorker(
    credentials: LoginCredentials,
): Promise<AuthResponse> {
    console.log('Attempting login with credentials:', {
        username: credentials.username,
        hasPassword: !!credentials.password,
    });

    try {
        const response = await fetch(`${API_BASE}/worker/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
        });

        const data: LoginResponse = await response.json();
        console.log('Login response data:', {
            success: !!data.accessToken,
            message: data.message,
        });

        if (!response.ok) {
            throw new Error(data.message || 'Invalid credentials');
        }

        if (!data.accessToken) {
            throw new Error('No access token received');
        }

        // Extract tenantId from the JWT
        const decodedToken: any = jwtDecode(data.accessToken);
        console.log('Decoded token:', {
            hasInfo: !!decodedToken?.info,
            hasTenantId: !!decodedToken?.info?.tenantId,
        });

        const tenantId = decodedToken?.info?.tenantId;

        if (!tenantId) {
            throw new Error('Unable to extract Tenant ID');
        }

        // Store tokens in localStorage
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        return {
            token: `${data.info.id}_${Date.now()}`,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            worker_id: data.info.id.toString(),
            worker_name: data.info.name,
            worker_info: data.info,
            tenantId: tenantId,
        };
    } catch (error) {
        console.error('Login error:', error);
        if (error instanceof Error) {
            throw error;
        }
        throw new Error('Login failed. Please try again.');
    }
}

export async function getShifts(): Promise<WorkerShift[]> {
    try {
        const url = `${API_BASE}/shifts/getshifts`;
        console.log('Fetching shifts URL:', url);

        const response = await fetchWithAuth(url);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response body:', errorText);
            throw new Error(
                `HTTP error! status: ${response.status}, body: ${errorText}`,
            );
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
            throw new Error('Invalid shifts data format');
        }

        return data;
    } catch (error) {
        console.error('Get shifts error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            timestamp: new Date().toISOString(),
        });
        throw error;
    }
}

export async function submitClockOut(
    token: string,
    workerId: string,
    shift: WorkerShift,
): Promise<void> {
    if (!token || !workerId || !shift) {
        throw new Error('Missing required clock out parameters');
    }

    try {
        const tenantId = getTenantId();
        const payload = {
            tenantId,
            workerId: parseInt(workerId, 10),
            clockin_time: Math.floor(shift.clockin_time / 1000),
            finishjob_time: shift.finishjob_time
                ? Math.floor(shift.finishjob_time / 1000)
                : undefined,
            clockout_time: shift.clockout_time
                ? Math.floor(shift.clockout_time / 1000)
                : undefined,
        };

        const response = await fetchWithAuth(`${API_BASE}/shifts/clock-out`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Clock out request failed');
        }

        await response.json();
    } catch (error) {
        console.error('Clock out error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            workerId,
            shift: {
                clockin_time: shift.clockin_time,
                finishjob_time: shift.finishjob_time,
                clockout_time: shift.clockout_time,
            },
        });
        throw error instanceof Error ? error : new Error('Failed to clock out');
    }
}

export async function getWorkOrders(
    token: string,
    workerId: string,
): Promise<WorkOrder> {
    try {
        if (!token || !workerId) {
            throw new Error('Missing required credentials');
        }

        // Get tenantId from the stored token
        const decodedToken: any = jwtDecode(token);
        const tenantId = decodedToken?.info?.tenantId;

        if (!tenantId) {
            throw new Error('Invalid tenant information');
        }

        const response = await fetchWithAuth(
            `${API_BASE}/orders/worker/${workerId}`,
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            if (response.status === 403) {
                throw new Error(
                    'You do not have permission to access these work orders',
                );
            }
            throw new Error(
                errorData?.message || 'Failed to fetch work orders',
            );
        }

        return response.json();
    } catch (error) {
        console.error('Get work orders error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            workerId,
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch work orders');
    }
}

export async function getExpensesMy(token: string): Promise<ExpensesResponse> {
    try {
        if (!token) {
            throw new Error('Authentication token is required');
        }

        const response = await fetchWithAuth(`${API_BASE}/expense/my`);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to fetch expenses');
        }

        return response.json();
    } catch (error) {
        console.error('Get expenses error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch expenses');
    }
}

export async function getExpenses(token: string): Promise<ExpensesResponse> {
    try {
        if (!token) {
            throw new Error('Authentication token is required');
        }

        const response = await fetchWithAuth(`${API_BASE}/expense`);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to fetch expenses');
        }

        return response.json();
    } catch (error) {
        console.error('Get expenses error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch expenses');
    }
}

export async function getExpenseCategories(token: string) {
    try {
        if (!token) {
            throw new Error('Authentication token is required');
        }

        const response = await fetchWithAuth(
            `${API_BASE}/expense-categories/get`,
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.message || 'Failed to fetch expense categories',
            );
        }

        return response.json();
    } catch (error) {
        console.error('Get expense categories error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch expense categories');
    }
}

export async function addExpense(
    token: string,
    workerId: string,
    amount: number,
    comment: string,
    date: string,
    categoryId?: number,
    orderId?: number,
    action: 'SPENDING' | 'PENALTY' = 'SPENDING',
    files?: File[],
): Promise<void> {
    try {
        if (!token || !workerId) {
            throw new Error('Missing required credentials');
        }

        // Create FormData object
        const formData = new FormData();
        formData.append('workerId', workerId);
        formData.append('amount', amount.toString());
        formData.append('action', action);
        formData.append('comment', comment);
        formData.append('date', date);

        if (categoryId !== undefined) {
            formData.append('categoryId', categoryId.toString());
        }

        if (orderId !== undefined) {
            formData.append('orderId', orderId.toString());
        }

        // Append files if they exist
        if (files && files.length > 0) {
            files.forEach((file) => {
                formData.append('files', file);
            });
        }

        const response = await fetchWithAuth(`${API_BASE}/expense`, {
            method: 'POST',
            body: formData,
            // Don't set any Content-Type header - let the browser handle it
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to add expense');
        }

        await response.json();
    } catch (error) {
        console.error('Add expense error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            workerId,
            amount,
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to add expense');
    }
}

export async function completeTask(
    token: string,
    workerId: string,
    taskId: number,
    startTime: number,
    endTime: number,
    spentTime: number,
): Promise<void> {
    try {
        if (!token || !workerId) {
            throw new Error('Missing required credentials');
        }

        const payload = {
            workerId: parseInt(workerId, 10),
            taskId,
            start_time: startTime,
            end_time: endTime,
            spent_time: spentTime,
        };

        const response = await fetchWithAuth(
            `${API_BASE}/task/status-complete`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            },
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to complete task');
        }

        await response.json();
    } catch (error) {
        console.error('Task completion error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            workerId,
            taskId,
            startTime,
            endTime,
            spentTime,
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to complete task');
    }
}

export async function getBidPhotos(reportId: number): Promise<any[]> {
    try {
        const response = await fetchWithAuth(
            `${API_BASE}/bid-photos/worker/${reportId}`,
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to fetch bid photos');
        }

        return response.json();
    } catch (error) {
        console.error('Get bid photos error:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            reportId,
            timestamp: new Date().toISOString(),
        });
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch bid photos');
    }
}

export async function getAllFolders(token: string): Promise<FolderDoc[]> {
    try {
        if (!token) {
            throw new Error('Authentication token is required');
        }

        const response = await fetchWithAuth(`${API_BASE}/forms-docs/folders`);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to fetch folders');
        }

        return response.json();
    } catch (error) {
        console.error('Get folders error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch folders');
    }
}

export async function getFolderContents(
    token: string,
    folderId: number,
): Promise<FolderDoc> {
    try {
        if (!token) {
            throw new Error('Authentication token is required');
        }

        const response = await fetchWithAuth(
            `${API_BASE}/forms-docs/folders/${folderId}`,
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.message || 'Failed to fetch folder contents',
            );
        }

        return response.json();
    } catch (error) {
        console.error('Get folder contents error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch folder contents');
    }
}

export async function getWorkOrder(token: string, reportId: number) {
    try {
        const response = await fetchWithAuth(`${API_BASE}/orders/${reportId}`);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to fetch work order');
        }

        const data = await response.json();
        return data.info;
    } catch (error) {
        console.error('Get work order error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to fetch work order');
    }
}

export async function addJobNote(
    token: string,
    reportId: number,
    noteText: string,
) {
    try {
        const response = await fetchWithAuth(`${API_BASE}/job-notes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ report_id: reportId, note_text: noteText }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to add note');
        }

        return response.json();
    } catch (error) {
        console.error('Add job note error:', error);
        throw error instanceof Error ? error : new Error('Failed to add note');
    }
}

export async function clockIn(workerId: number): Promise<void> {
    try {
        const clientRequestId = uuidv4();

        const response = await fetchWithAuth(`${API_BASE}/shifts/clock-in`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                workerId,
                clientRequestId: clientRequestId,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to clock in');
        }

        await response.json();
    } catch (error) {
        console.error('Clock in error:', error);
        throw error instanceof Error ? error : new Error('Failed to clock in');
    }
}

export async function finishJob(shiftId: number): Promise<void> {
    try {
        const clientRequestId = uuidv4();

        const response = await fetchWithAuth(`${API_BASE}/shifts/finish-job`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: shiftId,
                clientRequestId: clientRequestId,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to finish job');
        }

        await response.json();
    } catch (error) {
        console.error('Finish job error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to finish job');
    }
}

export async function clockOut(shiftId: number): Promise<void> {
    try {
        const clientRequestId = uuidv4();

        const response = await fetchWithAuth(`${API_BASE}/shifts/clock-out`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: shiftId,
                clientRequestId: clientRequestId,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to clock out');
        }

        await response.json();
    } catch (error) {
        console.error('Clock out error:', error);
        throw error instanceof Error ? error : new Error('Failed to clock out');
    }
}

// ===== Shift cache (module-level) =====
let shiftInFlight: Promise<WorkerShift | null> | null = null;
let shiftCache: WorkerShift | null = null;
let shiftCacheAt = 0;

// TTL кэша (мс). 3–5 секунд обычно достаточно, чтобы убрать "залп" при старте.
const SHIFT_CACHE_TTL = 5000;

// Ваш текущий getCurrentShift — без изменений
export async function getCurrentShift(
    token: string,
    workerId: number,
): Promise<WorkerShift | null> {
    try {
        const response = await fetchWithAuth(
            `${API_BASE}/shifts/current-shift?workerId=${workerId}`,
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.message || 'Failed to get current shift',
            );
        }

        const { hasShift, shiftStatus, shift } = await response.json();

        if (!hasShift) {
            return null;
        }

        // Convert backend timestamps (seconds) to frontend timestamps (milliseconds)
        const normalizedShift: WorkerShift = {
            ...shift,
            clockin_time: shift.clockin_time * 1000,
            finishjob_time: shift.finishjob_time
                ? shift.finishjob_time * 1000
                : undefined,
            clockout_time: shift.clockout_time
                ? shift.clockout_time * 1000
                : undefined,
            status: shiftStatus,
        };

        return normalizedShift;
    } catch (error) {
        console.error('Get current shift error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to get current shift');
    }
}

export async function getCurrentShiftCached(
    token: string,
    workerId: number,
    force = false,
): Promise<WorkerShift | null> {
    const now = Date.now();

    if (!force && shiftCacheAt > 0 && now - shiftCacheAt < SHIFT_CACHE_TTL) {
        return shiftCache;
    }

    if (!force && shiftInFlight) {
        return shiftInFlight;
    }

    shiftInFlight = (async () => {
        try {
            const res = await getCurrentShift(token, workerId);
            shiftCache = res;
            shiftCacheAt = Date.now();
            return res;
        } finally {
            shiftInFlight = null;
        }
    })();

    return shiftInFlight;
}

export function clearCurrentShiftCache() {
    shiftCache = null;
    shiftCacheAt = 0;
    shiftInFlight = null;
}

export async function startTask(
    token: string,
    workerId: number,
    taskId: number,
    startTime: number,
): Promise<void> {
    try {
        const response = await fetchWithAuth(`${API_BASE}/task/start`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                workerId,
                taskId,
                start_time: Math.floor(startTime / 1000),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to start task');
        }

        await response.json();
    } catch (error) {
        console.error('Start task error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to start task');
    }
}
export async function pauseTask(
    token: string,
    workerId: number,
    taskId: number,
    pause_time: number,
): Promise<void> {
    try {
        const response = await fetchWithAuth(`${API_BASE}/task/pause`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                workerId,
                taskId,
                pause_time: Math.floor(pause_time / 1000),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to start task');
        }

        await response.json();
    } catch (error) {
        console.error('Start task error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to start task');
    }
}
export async function resumeTask(
    token: string,
    workerId: number,
    taskId: number,
    start_time: number,
): Promise<void> {
    try {
        const response = await fetchWithAuth(`${API_BASE}/task/resume`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                workerId,
                taskId,
                start_time: Math.floor(start_time / 1000),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to start task');
        }

        await response.json();
    } catch (error) {
        console.error('Start task error:', error);
        throw error instanceof Error
            ? error
            : new Error('Failed to start task');
    }
}

// End Task
export async function endTask(
    token: string,
    workerId: number,
    taskId: number,
    startTime: number,
    endTime: number,
): Promise<void> {
    try {
        const response = await fetchWithAuth(`${API_BASE}/task/end`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                workerId,
                taskId,
                start_time: Math.floor(startTime / 1000),
                end_time: Math.floor(endTime / 1000),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to end task');
        }

        await response.json();
    } catch (error) {
        console.error('End task error:', error);
        throw error instanceof Error ? error : new Error('Failed to end task');
    }
}
